// App.jsx — Gestion des routes client + marchand + admin
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import Header from "./Header";
import Home from "./Home";
import Promotions from "./Promotions";
import Commandes from "./Commandes";
import Compte from "./Compte";
import LoginClient from "./LoginClient";
import SplashScreen from "./SplashScreen";
import Footer from "./Footer";
import Panier from "./Panier";
import DetailsCommande from "./DetailsCommande";
import PreCommande from "./PreCommande";
import CommandeEnvoyee from "./CommandeEnvoyee";
import MerchantSettings from "./MerchantSettings";
import { useEffect } from "react";
import { PushNotifications } from '@capacitor/push-notifications';
import axios from "axios";
import { LanguageProvider } from './LanguageContext';

// ✅ CATALOGUE DYNAMIQUE
import MerchantCatalogue from "./MerchantCatalogue";

// ================= CLIENT – PAGES COMPTE =================
import ChangerMotDePasse from "./ChangerMotDePasse";
import MesCoupons from "./MesCoupons";
import AideFaq from "./AideFaq";
import MesFavoris from "./MesFavoris"; // ✅ AJOUT UNIQUE
// =========================================================

import { CartProvider } from "./CartContext";

// ================= MARCHAND =================
import LoginMerchant from "./LoginMerchant";
import MerchantDashboard from "./MerchantDashboard";
import MerchantOrders from "./MerchantOrders";
import MerchantProducts from "./MerchantProducts";
import MerchantOrderDetails from "./MerchantOrderDetails";
import { isMerchantAuthenticated } from "./auth";
import MerchantSubscription from "./MerchantSubscription";
import MerchantPublicInfo from "./MerchantPublicInfo";

// ============================================

// ================= ADMIN =================
import AdminLayout from "./admin/AdminLayout";
import AdminLogin from "./admin/AdminLogin";
import AdminDashboard from "./admin/AdminDashboard";
import AdminUsers from "./admin/pages/AdminUsers";
import AdminMerchants from "./admin/pages/AdminMerchants";
import AdminOrders from "./admin/pages/AdminOrders";
import AdminSettings from "./admin/pages/AdminSettings";
import AdminSubscriptionPayments from "./admin/AdminSubscriptionPayments";
import AdminOrderDetails from "./admin/pages/AdminOrderDetails";
import AdminPromotions from "./admin/pages/AdminPromotions"; // Importe ta nouvelle page

// =========================================

// --------------------------------------------------
// 🔐 Route protégée marchand
// --------------------------------------------------
function MerchantRoute({ children }) {
  if (!isMerchantAuthenticated()) {
    return <Navigate to="/merchant-login" replace />;
  }
  return children;
}

// --------------------------------------------------
// 🔐 Route protégée admin
// --------------------------------------------------
function AdminRoute({ children }) {
  const token = localStorage.getItem("admin_token");
  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
}

// --------------------------------------------------
// Layout global (Header/Footer conditionnels)
// --------------------------------------------------
function LayoutWrapper() {
  const location = useLocation();

  const hideLayout =
    location.pathname.startsWith("/merchant") ||
    location.pathname.startsWith("/admin");

  return (
    <>
      {!hideLayout && <SplashScreen />}
      {!hideLayout && <Header />}

      <main className="min-h-screen">
        <Routes>
          {/* ================= CLIENT ================= */}
          <Route path="/" element={<Home />} />

          {/* ✅ ROUTE UNIQUE CATALOGUE DYNAMIQUE */}
          <Route path="/boutique/:id" element={<MerchantCatalogue />} />

          <Route path="/promotions" element={<Promotions />} />
          <Route path="/commandes" element={<Commandes />} />
          <Route path="/commandes/:id" element={<DetailsCommande />} />
          <Route path="/login-client" element={<LoginClient />} />
          <Route path="/compte" element={<Compte />} />

          <Route
            path="/client/changer-mot-de-passe"
            element={<ChangerMotDePasse />}
          />
          <Route path="/client/coupons" element={<MesCoupons />} />

          {/* ❤️ ROUTE MES FAVORIS — AJOUT */}
          <Route path="/client/favoris" element={<MesFavoris />} />

          <Route path="/aide-faq" element={<AideFaq />} />

          <Route path="/panier" element={<Panier />} />
          <Route path="/pre-commande" element={<PreCommande />} />
          <Route path="/commande-envoyee" element={<CommandeEnvoyee />} />

          {/* ================= MARCHAND ================= */}
          <Route path="/merchant-login" element={<LoginMerchant />} />

          <Route
            path="/merchant/dashboard"
            element={
              <MerchantRoute>
                <MerchantDashboard />
              </MerchantRoute>
            }
          />
          <Route
            path="/merchant/settings"
            element={
              <MerchantRoute>
                <MerchantSettings />
              </MerchantRoute>
            }
          />
          <Route
            path="/merchant/orders"
            element={
              <MerchantRoute>
                <MerchantOrders />
              </MerchantRoute>
            }
          />
          
          <Route
            path="/merchant/subscription"
            element={
              <MerchantRoute>
                <MerchantSubscription />
              </MerchantRoute>
            }
          />
          
          <Route
            path="/merchant/orders/:id"
            element={
              <MerchantRoute>
                <MerchantOrderDetails />
              </MerchantRoute>
            }
          />

          <Route
            path="/merchant/public-info"
            element={
              <MerchantRoute>
                <MerchantPublicInfo />
              </MerchantRoute>
            }
          />
          
          <Route
            path="/merchant/products"
            element={
              <MerchantRoute>
                <MerchantProducts />
              </MerchantRoute>
            }
          />

         {/* ================= ADMIN ================= */}
          <Route path="/admin/login" element={<AdminLogin />} />
             
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            {/* 1. L'index (le dashboard par défaut) */}
            <Route index element={<AdminDashboard />} />

            {/* 2. Pages existantes */}
            <Route path="users" element={<AdminUsers />} />
            <Route path="merchants" element={<AdminMerchants />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="orders/:orderNumber" element={<AdminOrderDetails />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="subscriptions" element={<AdminSubscriptionPayments />} />

            {/* 3. ✅ TA NOUVELLE PAGE PROMOTIONS */}
            <Route path="promotions" element={<AdminPromotions />} />
          </Route>

        </Routes> {/* ⬅️ C'ÉTAIT CETTE BALISE QUI MANQUAIT ! */}

        {!hideLayout && <Footer />}
      </main>
    </>
  );
}

// --------------------------------------------------
// App Root
// --------------------------------------------------
export default function App() {
  
  useEffect(() => {
    // 1. On vérifie si on est bien sur une plateforme native (iOS/Android)
    // Cela évite de faire planter le build Cloudflare ou le navigateur
    const isPushSupported = typeof window !== 'undefined' && 
                            window.Capacitor && 
                            window.Capacitor.isNativePlatform();

    if (!isPushSupported) {
      console.log("🌐 Environnement Web détecté : Push désactivé.");
      return; 
    }

    const setupNotifications = async () => {
      try {
        let permStatus = await PushNotifications.checkPermissions();

        if (permStatus.receive === 'prompt') {
          permStatus = await PushNotifications.requestPermissions();
        }

        if (permStatus.receive !== 'granted') {
          console.log("❌ Permission notifications refusée");
          return;
        }

        await PushNotifications.register();

        await PushNotifications.addListener('registration', async (token) => {
          console.log('✅ Token FCM généré :', token.value);
          
          const user = JSON.parse(localStorage.getItem("user"));
          if (user && user.phone) {
            try {
              // Note: Utilise ton URL process.env ou l'URL directe
              await axios.post(`${process.env.REACT_APP_API_URL}/api/users/update-fcm-token`, {
                phone: user.phone,
                fcmToken: token.value
              });
              console.log("🚀 Token envoyé avec succès au backend");
            } catch (err) {
              console.error("❌ Erreur envoi token au backend", err);
            }
          }
        });

        await PushNotifications.createChannel({
          id: 'fcm_default_channel',
          name: 'Notifications Commandes',
          description: 'Notifications pour le suivi de vos commandes',
          importance: 5,
          visibility: 1,
          vibration: true
        });
      } catch (error) {
        console.error("❌ Erreur lors de l'initialisation des notifications:", error);
      }
    };

    setupNotifications();
  }, []);

  return (
    // ✅ AJOUT : On enveloppe tout dans le LanguageProvider
    <LanguageProvider>
      <CartProvider>
        <Router>
          <LayoutWrapper />
        </Router>
      </CartProvider>
    </LanguageProvider>
  );
}
